import type { FieldValues, Path, UseFormSetError } from 'react-hook-form'

/**
 * Backend egységes hibaválasz-envelope (DEC-ERROR-001).
 * A `details.errors.{field}: string[]` map a 422-es validation_failed
 * response-ban érkezik; minden más hibatípus esetén `details = {}`.
 */
export interface ApiErrorEnvelope {
  code: string
  message: string
  details: Record<string, unknown>
  trace_id: string
}

export function isApiErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.code === 'string' &&
    typeof v.message === 'string' &&
    typeof v.trace_id === 'string' &&
    typeof v.details === 'object' &&
    v.details !== null
  )
}

interface ApplyResult {
  handled: boolean
  traceId?: string
  message?: string
  code?: string
}

/**
 * 422 `validation_failed` envelope → react-hook-form field-szintű hibák
 * (DEC-FE-007 / DEC-FE-020). A nem-field hibákat (`unauthenticated`,
 * `forbidden`, `rate_limited` stb.) a hívó kezelje globális
 * error-banner-rel a `code` és `traceId` alapján.
 */
export function applyApiErrorsToForm<TFieldValues extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<TFieldValues>,
): ApplyResult {
  if (!isApiErrorEnvelope(error)) {
    return { handled: false }
  }
  if (error.code !== 'validation_failed') {
    return {
      handled: false,
      traceId: error.trace_id,
      message: error.message,
      code: error.code,
    }
  }
  const fieldErrors = (error.details as { errors?: Record<string, unknown> }).errors
  if (fieldErrors && typeof fieldErrors === 'object') {
    for (const [field, messages] of Object.entries(fieldErrors)) {
      if (Array.isArray(messages) && messages.length > 0 && typeof messages[0] === 'string') {
        setError(field as Path<TFieldValues>, {
          type: 'server',
          message: messages[0],
        })
      }
    }
  }
  return {
    handled: true,
    traceId: error.trace_id,
    message: error.message,
    code: error.code,
  }
}
